<script setup lang="ts">
import type { Fields } from '@voix/composables/useSlice'
import type {
  ElementInterface,
  SliceInterface,
  SliceSettingsInterface,
} from '@voix/types'

import type { ComputedRef, Ref } from 'vue'

import { useNuxtApp, useRuntimeConfig } from '#imports'
import { useEvent } from '@voix/composables/useEventBus'
import { useSlice } from '@voix/composables/useSlice'
import { useUtils } from '@voix/composables/useUtils'

import { useAdminStore } from '@voix/store/adminStore'

import { useSliceStore } from '@voix/store/sliceStore'

import { useDebounceFn, useWindowSize } from '@vueuse/core'
import { inView } from 'motion'
import {
  computed,
  provide,
  ref,
  watch,
} from 'vue'
import { findBreakpointSettings } from '../../composables/useBreakpoints'

import {
  buildColorStyles,
  buildFlexStyles,
  buildGridStyles,
  buildMarginStyles,
  buildMaxWidthStyles,
  buildPaddingStyles,
} from '../../composables/useStyle'
import { usePageStore } from '../../store/pageStore'

defineOptions({ inheritAttrs: false })
const props = defineProps<{
  slice: SliceInterface
}>()
// Import all the slices dynamically
// voixSliceTransform.ts will automatically add the import statements
// like `import { LazySlicesCircleSlideshow } from '#components'` to
// this file at the comment below

// DO NOT REMOVE THESE LINES WHERE WE DEFINE components
let components: Record<string, any> = {}

import { LazySlicesAgentsPortalDashboard } from '#components'
import { LazySlicesAgentsPortalHero } from '#components'
import { LazySlicesAgentsPortalLogin } from '#components'
import { LazySlicesAgentsPortalLoginRequiredButton } from '#components'
import { LazySlicesAgentsPortalPopup } from '#components'
import { LazySlicesAgentsPortalSection } from '#components'
import { LazySlicesAgentsPortalSproutloudSsoBlock } from '#components'
import { LazySlicesAgentsPortalStaff } from '#components'
import { LazySlicesAgentsPortalWhiteContentIconFeatureCard } from '#components'
import { LazySlicesAgentsPortalSpecialsWithFilters } from '#components'
import { LazySlicesBlocksBlockOverImage } from '#components'
import { LazySlicesBlocksBlocksHero } from '#components'
import { LazySlicesBlocksBrandCard } from '#components'
import { LazySlicesBlocksBlockLink } from '#components'
import { LazySlicesBlocksBlockSpecials } from '#components'
import { LazySlicesBlocksBrandResortFilters } from '#components'
import { LazySlicesBlocksBrandResortListing } from '#components'
import { LazySlicesBlocksImageSliderWithContent } from '#components'
import { LazySlicesBlocksBrandsSection } from '#components'
import { LazySlicesBlocksIncludedWithStay } from '#components'
import { LazySlicesBlocksFeaturesSection } from '#components'
import { LazySlicesBlocksFeaturedPlaceSection } from '#components'
import { LazySlicesBlocksSoloMedia } from '#components'
import { LazySlicesBlocksTermsConditionsSection } from '#components'
import { LazySlicesBonitaAllIncluded } from '#components'
import { LazySlicesBlocksNewsletterFormSection } from '#components'
import { LazySlicesBonitaBackgroundImageWithCard } from '#components'
import { LazySlicesBonitaDoubleImageIntro } from '#components'
import { LazySlicesBonitaFeature } from '#components'
import { LazySlicesBonitaFeatureGalleryImage } from '#components'
import { LazySlicesBonitaFeaturesBlock } from '#components'
import { LazySlicesBonitaGalleriesMagazineSlider } from '#components'
import { LazySlicesBonitaImageFlankingTitleCopy } from '#components'
import { LazySlicesBonitaFeatures } from '#components'
import { LazySlicesBonitaImageTitleCopyCard } from '#components'
import { LazySlicesBonitaTwoColumnContainer } from '#components'
import { LazySlicesGlobalAnniversarySweepstakesForm } from '#components'
import { LazySlicesGlobalBookingDatesLimiter } from '#components'
import { LazySlicesGlobalBookingExcludeResorts } from '#components'
import { LazySlicesGlobalContactBlock } from '#components'
import { LazySlicesGlobalCountdownClock } from '#components'
import { LazySlicesGlobalDownloadLink } from '#components'
import { LazySlicesGlobalFactSheets } from '#components'
import { LazySlicesGlobalGhostButtonLink } from '#components'
import { LazySlicesGlobalGlobalImage } from '#components'
import { LazySlicesGlobalIframe } from '#components'
import { LazySlicesGlobalImage } from '#components'
import { LazySlicesGlobalHtml5Video } from '#components'
import { LazySlicesGlobalImageCard } from '#components'
import { LazySlicesGlobalLogo } from '#components'
import { LazySlicesGlobalRmCartButtonCode } from '#components'
import { LazySlicesGlobalPromoCoupon } from '#components'
import { LazySlicesGlobalNumberedImageContentCard } from '#components'
import { LazySlicesGlobalSafeStayBar } from '#components'
import { LazySlicesGlobalSealGraphicWithText } from '#components'
import { LazySlicesGlobalSafeStay } from '#components'
import { LazySlicesGlobalSkewedThreeLines } from '#components'
import { LazySlicesGlobalSignUpPopUp } from '#components'
import { LazySlicesGlobalSliderImage } from '#components'
import { LazySlicesGlobalSliderImageCard } from '#components'
import { LazySlicesGlobalStyleGuide } from '#components'
import { LazySlicesGlobalYoutube } from '#components'
import { LazySlicesGlobalStyledNumberWithText } from '#components'
import { LazySlicesGlobalYoutubePlayer } from '#components'
import { LazySlicesGlobalBookingInlineBookingWidget } from '#components'
import { LazySlicesGlobalContainersBackgroundImageContainer } from '#components'
import { LazySlicesGlobalContainersContentContainer } from '#components'
import { LazySlicesGlobalBookingInlineFlightBookingWidget } from '#components'
import { LazySlicesGlobalBookingLargeBookingWidget } from '#components'
import { LazySlicesGlobalContainersStrippedList } from '#components'
import { LazySlicesGlobalContainersTitleListColumn } from '#components'
import { LazySlicesGlobalContainersFullWidthContainer } from '#components'
import { LazySlicesGlobalContainersGridOfCards } from '#components'
import { LazySlicesGlobalContainersTwoColContainer } from '#components'
import { LazySlicesGlobalCredibilityCredibilitySlider } from '#components'
import { LazySlicesGlobalFormsAgentSearch } from '#components'
import { LazySlicesGlobalErrorsFourOhFour } from '#components'
import { LazySlicesGlobalFormsBrochureRequest } from '#components'
import { LazySlicesGlobalFormsIdMe } from '#components'
import { LazySlicesGlobalFormsGroups } from '#components'
import { LazySlicesGlobalFormsGroupsShort } from '#components'
import { LazySlicesGlobalFormsHandbookLogin } from '#components'
import { LazySlicesGlobalGalleriesGridGallery } from '#components'
import { LazySlicesGlobalGalleriesMagazineSlide } from '#components'
import { LazySlicesGlobalGalleriesMagazineSlider } from '#components'
import { LazySlicesGlobalGalleriesGridGalleryElementsImage } from '#components'
import { LazySlicesGlobalGalleriesGridGalleryElementsGridImage } from '#components'
import { LazySlicesGlobalHeroesHeroImage } from '#components'
import { LazySlicesGlobalGalleriesGridGalleryElementsContentBox } from '#components'
import { LazySlicesGlobalHeroesHeroSlider } from '#components'
import { LazySlicesGlobalHeroesVimeoHero } from '#components'
import { LazySlicesGlobalIntroductionsColumnsAndPictures } from '#components'
import { LazySlicesGlobalIntroductionsFullWidthWysiwyg } from '#components'
import { LazySlicesGlobalIntroductionsTitleParagraph } from '#components'
import { LazySlicesGlobalIntroductionsTitleSubtitle } from '#components'
import { LazySlicesGlobalOverridesShowCovid19Info } from '#components'
import { LazySlicesGlobalTablesTable } from '#components'
import { LazySlicesGlobalSlidersGeneralSlider } from '#components'
import { LazySlicesGlobalTablesTextRow } from '#components'
import { LazySlicesHyattAmenaties } from '#components'
import { LazySlicesGlobalSmallPiecesLearnMoreModal } from '#components'
import { LazySlicesGlobalTablesContactInformationRow } from '#components'
import { LazySlicesHyattBanner } from '#components'
import { LazySlicesHyattBackgroundImageContentBlock } from '#components'
import { LazySlicesHyattIntroductionTwoColumn } from '#components'
import { LazySlicesHyattLeftRightHalfBlock } from '#components'
import { LazySlicesHyattLeftRightCurveBlock } from '#components'
import { LazySlicesHyattRainbowBar } from '#components'
import { LazySlicesHyattLeftRightBlockSuite } from '#components'
import { LazySlicesHyattSpecialOffer } from '#components'
import { LazySlicesHyattSwooshMaskedImagesAndColumns } from '#components'
import { LazySlicesHyattTwentyTwentyTwoAllIncluded } from '#components'
import { LazySlicesHyattTwentyTwentyTwoFracturedGallery } from '#components'
import { LazySlicesHyattTwentyTwentyTwoMagazineOverlay } from '#components'
import { LazySlicesHyattTwentyTwentyTwoNavyBlockOverlay } from '#components'
import { LazySlicesHyattTwentyTwentyTwoSignatureTitle } from '#components'
import { LazySlicesHyattTwentyTwentyTwoSmallImageSmallCopy } from '#components'
import { LazySlicesHyattTwentyTwentyTwoSpecialOffer } from '#components'
import { LazySlicesHyattTwentyTwentyTwoTwoImageDescriptionOverlay } from '#components'
import { LazySlicesHyattTwentyTwentyTwoSlantedGallery } from '#components'
import { LazySlicesInfinityLuxuryContactForm } from '#components'
import { LazySlicesInfinityLuxuryInfinityHero } from '#components'
import { LazySlicesInfinityLuxuryInfinityTextSlice } from '#components'
import { LazySlicesInfinityLuxuryMagazineLayout } from '#components'
import { LazySlicesInfinityLuxuryBookingMemberBookingWidget } from '#components'
import { LazySlicesHyattSmallPiecesSuiteDetails } from '#components'
import { LazySlicesKimptonAllIncluded } from '#components'
import { LazySlicesKimptonBanner } from '#components'
import { LazySlicesKimptonCopyCard } from '#components'
import { LazySlicesKimptonExpandableContentBlock } from '#components'
import { LazySlicesKimptonHorizontalCard } from '#components'
import { LazySlicesKimptonHero } from '#components'
import { LazySlicesKimptonImageCard } from '#components'
import { LazySlicesKimptonIntro } from '#components'
import { LazySlicesKimptonMagazineCard } from '#components'
import { LazySlicesKimptonScrollingGallery } from '#components'
import { LazySlicesKimptonScrollingGalleryImage } from '#components'
import { LazySlicesKimptonPromoGrid } from '#components'
import { LazySlicesKimptonStandardContentBlock } from '#components'
import { LazySlicesKimptonStandardCopy } from '#components'
import { LazySlicesKimptonTwoColumns } from '#components'
import { LazySlicesPanamaJackAlwaysIncluded } from '#components'
import { LazySlicesPlayaCaresFullWidthCountries } from '#components'
import { LazySlicesPlayaCaresFullWidthStats } from '#components'
import { LazySlicesPlayaCollectionAllIncluded } from '#components'
import { LazySlicesPlayaCollectionAwardsSlider } from '#components'
import { LazySlicesPlayaCollectionBlockOverImage } from '#components'
import { LazySlicesPlayaCollectionCallToAction } from '#components'
import { LazySlicesPlayaCollectionCenteredTextParallax } from '#components'
import { LazySlicesPlayaCollectionColorBarTitleParagraph } from '#components'
import { LazySlicesPlayaCollectionContentGrid } from '#components'
import { LazySlicesPlayaCollectionFeatures } from '#components'
import { LazySlicesPlayaCollectionHotelList } from '#components'
import { LazySlicesPlayaCollectionTestimonial } from '#components'
import { LazySlicesPlayaCollectionGridItemTitleDescription } from '#components'
import { LazySlicesPlayaCollectionHero } from '#components'
import { LazySlicesPlayaCollectionBookingMemberBookingWidget } from '#components'
import { LazySlicesPlayaCollectionThreeImageCards } from '#components'
import { LazySlicesPlayaCollectionTwoPaneTextImage } from '#components'
import { LazySlicesPlayaCollectionTitleDescription } from '#components'
import { LazySlicesPlayaresortsContactRow } from '#components'
import { LazySlicesPlayaresortsImageTitleCopyLink } from '#components'
import { LazySlicesPlayaresortsCheckInOutTimes } from '#components'
import { LazySlicesPlayaresortsLeftRightImageWithText } from '#components'
import { LazySlicesPlayaresortsLeftRightBlockSuite } from '#components'
import { LazySlicesPlayaresortsVerticalImageCard } from '#components'
import { LazySlicesPlayaresortsTitleWysiwyg } from '#components'
import { LazySlicesPlayaresortsPlayaresortsLeftRightBlockSuite } from '#components'
import { LazySlicesPlayaresortsWeWantYourInput } from '#components'
import { LazySlicesPlayaresortsWideImageBgSmallWhiteBox } from '#components'
import { LazySlicesPlayaresortsBlogPostsBlogAll } from '#components'
import { LazySlicesPlayaresortsBlogPostsBlogPostDetails } from '#components'
import { LazySlicesPlayaresortsBrandsRowsWithFilters } from '#components'
import { LazySlicesPlayaresortsPressReleasesPressReleasesAll } from '#components'
import { LazySlicesPlayaresortsPressReleasesPressReleasesSingle } from '#components'
import { LazySlicesPlayaresortsPrivacyPolicyTableRow } from '#components'
import { LazySlicesPlayaresortsResortsMeetingFacilitiesExplorer } from '#components'
import { LazySlicesPlayaresortsPrivacyPolicyTable } from '#components'
import { LazySlicesPlayaresortsSpecialOffersSpecialOfferBlock } from '#components'
import { LazySlicesPlayaresortsSpecialOffersSpecialOfferCard } from '#components'
import { LazySlicesPlayaresortsSpecialOffersPageSpecials } from '#components'
import { LazySlicesPlayaresortsSpecialOffersSpecialOfferHorizontal } from '#components'
import { LazySlicesPlayaresortsSpecialOffersSpecialOfferSlider } from '#components'
import { LazySlicesSanctuaryContact } from '#components'
import { LazySlicesPlayaresortsSpecialOffersSpecialOfferGrid } from '#components'
import { LazySlicesSanctuaryImageBlock } from '#components'
import { LazySlicesSanctuaryFullWidthSpecialOffer } from '#components'
import { LazySlicesSanctuaryFadeColorBlock } from '#components'
import { LazySlicesSanctuaryFeathersDivider } from '#components'
import { LazySlicesSanctuaryGoldPromotion } from '#components'
import { LazySlicesSanctuaryIncludedWithStay } from '#components'
import { LazySlicesSanctuaryIntroductionsTitleParagraph } from '#components'
import { LazySlicesSanctuaryRoomListing } from '#components'
import { LazySlicesSanctuarySpecialOffer } from '#components'
import { LazySlicesSanctuaryTwoColumnCopyAndImage } from '#components'
import { LazySlicesSanctuaryLeftRightHalfBlock } from '#components'
import { LazySlicesSandpiperAboutRoomes } from '#components'
import { LazySlicesSandpiperAccordionCopy } from '#components'
import { LazySlicesSandpiperActivityGrid } from '#components'
import { LazySlicesSanctuaryVideoTakeover } from '#components'
import { LazySlicesSandpiperActivityGridCard } from '#components'
import { LazySlicesSandpiperAlertBlock } from '#components'
import { LazySlicesSandpiperBackgroundShape } from '#components'
import { LazySlicesSandpiperColorCardGrid } from '#components'
import { LazySlicesSandpiperColorCardGridCard } from '#components'
import { LazySlicesSandpiperCopyBesideImages } from '#components'
import { LazySlicesSandpiperGeneralTitleAndCopy } from '#components'
import { LazySlicesSandpiperMap } from '#components'
import { LazySlicesSandpiperRoomType } from '#components'
import { LazySlicesSandpiperRoomTypes } from '#components'
import { LazySlicesSandpiperSandpiperHeroImage } from '#components'
import { LazySlicesSandpiperIncludedWithStay } from '#components'
import { LazySlicesSandpiperRoomTypeFilter } from '#components'
import { LazySlicesSandpiperSandpiperSpecialOfferSlider } from '#components'
import { LazySlicesSeadustContentSeadustBlog } from '#components'
import { LazySlicesSeadustContentSeadustBlogDetails } from '#components'
import { LazySlicesSeadustContentSeadustConferenceRoomFeature } from '#components'
import { LazySlicesSeadustContentSeadustContactForm } from '#components'
import { LazySlicesSeadustContentSeadustFullWidthGallery } from '#components'
import { LazySlicesSeadustContentSeadustConferenceRoom } from '#components'
import { LazySlicesSeadustContentSeadustGallery } from '#components'
import { LazySlicesSeadustContentSeadustGalleryList } from '#components'
import { LazySlicesSeadustContentSeadustGalleryListBlock } from '#components'
import { LazySlicesSeadustContentSeadustFullWidthGalleryImage } from '#components'
import { LazySlicesSeadustContentSeadustGenericFullWidthBgWhiteBox } from '#components'
import { LazySlicesSeadustContentSeadustGenericListItem } from '#components'
import { LazySlicesSeadustContentSeadustGenericListItemImage } from '#components'
import { LazySlicesSeadustContentSeadustMap } from '#components'
import { LazySlicesSeadustContentSeadustRestaurantList } from '#components'
import { LazySlicesSeadustContentSeadustRestaurantListBlock } from '#components'
import { LazySlicesSeadustContentSeadustRightParagraphGridImages } from '#components'
import { LazySlicesSeadustContentSeadustRoomDetails } from '#components'
import { LazySlicesSeadustContentSeadustSandyThreeColGridItem } from '#components'
import { LazySlicesSeadustContentSeadustServicesIconsList } from '#components'
import { LazySlicesSeadustContentSeadustSandyThreeColGrid } from '#components'
import { LazySlicesSeadustContentSeadustSafeVideoNavigation } from '#components'
import { LazySlicesSeadustContentSeadustServicesIconsListItem } from '#components'
import { LazySlicesSeadustContentSeadustSitemap } from '#components'
import { LazySlicesSeadustContentSeadustTitleParagraph } from '#components'
import { LazySlicesSeadustContentSeadustWeddingsForm } from '#components'
import { LazySlicesSeadustGraphicalLinksSeadustFiveImageGridLinks } from '#components'
import { LazySlicesSeadustFormsSeadustConferenceRoomRequestForm } from '#components'
import { LazySlicesSeadustSlidersFullWidthSlide } from '#components'
import { LazySlicesSeadustGraphicalLinksSeadustFiftyFiftyImageLinks } from '#components'
import { LazySlicesSeadustSlidersSeadustBlocksSlide } from '#components'
import { LazySlicesSeadustSlidersFullWidth } from '#components'
import { LazySlicesSeadustSlidersSeadustBlocksSlider } from '#components'
import { LazySlicesSeadustSlidersSeadustParalaxBlocksSlide } from '#components'
import { LazySlicesSeadustSlidersSeadustLeftAlignContentSlide } from '#components'
import { LazySlicesSeadustSlidersSeadustParalaxBlocksSlider } from '#components'
import { LazySlicesSeadustSlidersSeadustLeftAlignContentWithSlider } from '#components'
import { LazySlicesSeadustThreeSixtyToursSeadustSpaziousTour } from '#components'
import { LazySlicesWeddingsBgSideContentCallToAction } from '#components'
import { LazySlicesWeddingsContactDetails } from '#components'
import { LazySlicesWeddingsFullWidthImage } from '#components'
import { LazySlicesWeddingsCard } from '#components'
import { LazySlicesWeddingsPackagesSlider } from '#components'
import { LazySlicesWeddingsPackagesSlide } from '#components'
import { LazySlicesWeddingsPatternedGrid } from '#components'
import { LazySlicesWeddingsTwoColumnIntroduction } from '#components'
import { LazySlicesWeddingsVenueSlide } from '#components'
import { LazySlicesWeddingsTitleParagraphDivider } from '#components'
import { LazySlicesWeddingsVenueSlider } from '#components'
import { LazySlicesWyndhamCardOverImages } from '#components'
import { LazySlicesWyndhamDualImageCards } from '#components'
import { LazySlicesWyndhamFullWidthImageCardOnBottom } from '#components'
import { LazySlicesWyndhamHideSignUpToSave } from '#components'
import { LazySlicesWyndhamHideWyndhamInlineBookingWidget } from '#components'
import { LazySlicesWyndhamInlineBookingWidget } from '#components'
import { LazySlicesWyndhamTallSixImageCards } from '#components'
import { LazySlicesWyndhamTallThreeImageCards } from '#components'

components = {
  LazySlicesAgentsPortalDashboard,
  LazySlicesAgentsPortalHero,
  LazySlicesAgentsPortalLogin,
  LazySlicesAgentsPortalLoginRequiredButton,
  LazySlicesAgentsPortalPopup,
  LazySlicesAgentsPortalSection,
  LazySlicesAgentsPortalSproutloudSsoBlock,
  LazySlicesAgentsPortalStaff,
  LazySlicesAgentsPortalWhiteContentIconFeatureCard,
  LazySlicesAgentsPortalSpecialsWithFilters,
  LazySlicesBlocksBlockOverImage,
  LazySlicesBlocksBlocksHero,
  LazySlicesBlocksBrandCard,
  LazySlicesBlocksBlockLink,
  LazySlicesBlocksBlockSpecials,
  LazySlicesBlocksBrandResortFilters,
  LazySlicesBlocksBrandResortListing,
  LazySlicesBlocksImageSliderWithContent,
  LazySlicesBlocksBrandsSection,
  LazySlicesBlocksIncludedWithStay,
  LazySlicesBlocksFeaturesSection,
  LazySlicesBlocksFeaturedPlaceSection,
  LazySlicesBlocksSoloMedia,
  LazySlicesBlocksTermsConditionsSection,
  LazySlicesBonitaAllIncluded,
  LazySlicesBlocksNewsletterFormSection,
  LazySlicesBonitaBackgroundImageWithCard,
  LazySlicesBonitaDoubleImageIntro,
  LazySlicesBonitaFeature,
  LazySlicesBonitaFeatureGalleryImage,
  LazySlicesBonitaFeaturesBlock,
  LazySlicesBonitaGalleriesMagazineSlider,
  LazySlicesBonitaImageFlankingTitleCopy,
  LazySlicesBonitaFeatures,
  LazySlicesBonitaImageTitleCopyCard,
  LazySlicesBonitaTwoColumnContainer,
  LazySlicesGlobalAnniversarySweepstakesForm,
  LazySlicesGlobalBookingDatesLimiter,
  LazySlicesGlobalBookingExcludeResorts,
  LazySlicesGlobalContactBlock,
  LazySlicesGlobalCountdownClock,
  LazySlicesGlobalDownloadLink,
  LazySlicesGlobalFactSheets,
  LazySlicesGlobalGhostButtonLink,
  LazySlicesGlobalGlobalImage,
  LazySlicesGlobalIframe,
  LazySlicesGlobalImage,
  LazySlicesGlobalHtml5Video,
  LazySlicesGlobalImageCard,
  LazySlicesGlobalLogo,
  LazySlicesGlobalRmCartButtonCode,
  LazySlicesGlobalPromoCoupon,
  LazySlicesGlobalNumberedImageContentCard,
  LazySlicesGlobalSafeStayBar,
  LazySlicesGlobalSealGraphicWithText,
  LazySlicesGlobalSafeStay,
  LazySlicesGlobalSkewedThreeLines,
  LazySlicesGlobalSignUpPopUp,
  LazySlicesGlobalSliderImage,
  LazySlicesGlobalSliderImageCard,
  LazySlicesGlobalStyleGuide,
  LazySlicesGlobalYoutube,
  LazySlicesGlobalStyledNumberWithText,
  LazySlicesGlobalYoutubePlayer,
  LazySlicesGlobalBookingInlineBookingWidget,
  LazySlicesGlobalContainersBackgroundImageContainer,
  LazySlicesGlobalContainersContentContainer,
  LazySlicesGlobalBookingInlineFlightBookingWidget,
  LazySlicesGlobalBookingLargeBookingWidget,
  LazySlicesGlobalContainersStrippedList,
  LazySlicesGlobalContainersTitleListColumn,
  LazySlicesGlobalContainersFullWidthContainer,
  LazySlicesGlobalContainersGridOfCards,
  LazySlicesGlobalContainersTwoColContainer,
  LazySlicesGlobalCredibilityCredibilitySlider,
  LazySlicesGlobalFormsAgentSearch,
  LazySlicesGlobalErrorsFourOhFour,
  LazySlicesGlobalFormsBrochureRequest,
  LazySlicesGlobalFormsIdMe,
  LazySlicesGlobalFormsGroups,
  LazySlicesGlobalFormsGroupsShort,
  LazySlicesGlobalFormsHandbookLogin,
  LazySlicesGlobalGalleriesGridGallery,
  LazySlicesGlobalGalleriesMagazineSlide,
  LazySlicesGlobalGalleriesMagazineSlider,
  LazySlicesGlobalGalleriesGridGalleryElementsImage,
  LazySlicesGlobalGalleriesGridGalleryElementsGridImage,
  LazySlicesGlobalHeroesHeroImage,
  LazySlicesGlobalGalleriesGridGalleryElementsContentBox,
  LazySlicesGlobalHeroesHeroSlider,
  LazySlicesGlobalHeroesVimeoHero,
  LazySlicesGlobalIntroductionsColumnsAndPictures,
  LazySlicesGlobalIntroductionsFullWidthWysiwyg,
  LazySlicesGlobalIntroductionsTitleParagraph,
  LazySlicesGlobalIntroductionsTitleSubtitle,
  LazySlicesGlobalOverridesShowCovid19Info,
  LazySlicesGlobalTablesTable,
  LazySlicesGlobalSlidersGeneralSlider,
  LazySlicesGlobalTablesTextRow,
  LazySlicesHyattAmenaties,
  LazySlicesGlobalSmallPiecesLearnMoreModal,
  LazySlicesGlobalTablesContactInformationRow,
  LazySlicesHyattBanner,
  LazySlicesHyattBackgroundImageContentBlock,
  LazySlicesHyattIntroductionTwoColumn,
  LazySlicesHyattLeftRightHalfBlock,
  LazySlicesHyattLeftRightCurveBlock,
  LazySlicesHyattRainbowBar,
  LazySlicesHyattLeftRightBlockSuite,
  LazySlicesHyattSpecialOffer,
  LazySlicesHyattSwooshMaskedImagesAndColumns,
  LazySlicesHyattTwentyTwentyTwoAllIncluded,
  LazySlicesHyattTwentyTwentyTwoFracturedGallery,
  LazySlicesHyattTwentyTwentyTwoMagazineOverlay,
  LazySlicesHyattTwentyTwentyTwoNavyBlockOverlay,
  LazySlicesHyattTwentyTwentyTwoSignatureTitle,
  LazySlicesHyattTwentyTwentyTwoSmallImageSmallCopy,
  LazySlicesHyattTwentyTwentyTwoSpecialOffer,
  LazySlicesHyattTwentyTwentyTwoTwoImageDescriptionOverlay,
  LazySlicesHyattTwentyTwentyTwoSlantedGallery,
  LazySlicesInfinityLuxuryContactForm,
  LazySlicesInfinityLuxuryInfinityHero,
  LazySlicesInfinityLuxuryInfinityTextSlice,
  LazySlicesInfinityLuxuryMagazineLayout,
  LazySlicesInfinityLuxuryBookingMemberBookingWidget,
  LazySlicesHyattSmallPiecesSuiteDetails,
  LazySlicesKimptonAllIncluded,
  LazySlicesKimptonBanner,
  LazySlicesKimptonCopyCard,
  LazySlicesKimptonExpandableContentBlock,
  LazySlicesKimptonHorizontalCard,
  LazySlicesKimptonHero,
  LazySlicesKimptonImageCard,
  LazySlicesKimptonIntro,
  LazySlicesKimptonMagazineCard,
  LazySlicesKimptonScrollingGallery,
  LazySlicesKimptonScrollingGalleryImage,
  LazySlicesKimptonPromoGrid,
  LazySlicesKimptonStandardContentBlock,
  LazySlicesKimptonStandardCopy,
  LazySlicesKimptonTwoColumns,
  LazySlicesPanamaJackAlwaysIncluded,
  LazySlicesPlayaCaresFullWidthCountries,
  LazySlicesPlayaCaresFullWidthStats,
  LazySlicesPlayaCollectionAllIncluded,
  LazySlicesPlayaCollectionAwardsSlider,
  LazySlicesPlayaCollectionBlockOverImage,
  LazySlicesPlayaCollectionCallToAction,
  LazySlicesPlayaCollectionCenteredTextParallax,
  LazySlicesPlayaCollectionColorBarTitleParagraph,
  LazySlicesPlayaCollectionContentGrid,
  LazySlicesPlayaCollectionFeatures,
  LazySlicesPlayaCollectionHotelList,
  LazySlicesPlayaCollectionTestimonial,
  LazySlicesPlayaCollectionGridItemTitleDescription,
  LazySlicesPlayaCollectionHero,
  LazySlicesPlayaCollectionBookingMemberBookingWidget,
  LazySlicesPlayaCollectionThreeImageCards,
  LazySlicesPlayaCollectionTwoPaneTextImage,
  LazySlicesPlayaCollectionTitleDescription,
  LazySlicesPlayaresortsContactRow,
  LazySlicesPlayaresortsImageTitleCopyLink,
  LazySlicesPlayaresortsCheckInOutTimes,
  LazySlicesPlayaresortsLeftRightImageWithText,
  LazySlicesPlayaresortsLeftRightBlockSuite,
  LazySlicesPlayaresortsVerticalImageCard,
  LazySlicesPlayaresortsTitleWysiwyg,
  LazySlicesPlayaresortsPlayaresortsLeftRightBlockSuite,
  LazySlicesPlayaresortsWeWantYourInput,
  LazySlicesPlayaresortsWideImageBgSmallWhiteBox,
  LazySlicesPlayaresortsBlogPostsBlogAll,
  LazySlicesPlayaresortsBlogPostsBlogPostDetails,
  LazySlicesPlayaresortsBrandsRowsWithFilters,
  LazySlicesPlayaresortsPressReleasesPressReleasesAll,
  LazySlicesPlayaresortsPressReleasesPressReleasesSingle,
  LazySlicesPlayaresortsPrivacyPolicyTableRow,
  LazySlicesPlayaresortsResortsMeetingFacilitiesExplorer,
  LazySlicesPlayaresortsPrivacyPolicyTable,
  LazySlicesPlayaresortsSpecialOffersSpecialOfferBlock,
  LazySlicesPlayaresortsSpecialOffersSpecialOfferCard,
  LazySlicesPlayaresortsSpecialOffersPageSpecials,
  LazySlicesPlayaresortsSpecialOffersSpecialOfferHorizontal,
  LazySlicesPlayaresortsSpecialOffersSpecialOfferSlider,
  LazySlicesSanctuaryContact,
  LazySlicesPlayaresortsSpecialOffersSpecialOfferGrid,
  LazySlicesSanctuaryImageBlock,
  LazySlicesSanctuaryFullWidthSpecialOffer,
  LazySlicesSanctuaryFadeColorBlock,
  LazySlicesSanctuaryFeathersDivider,
  LazySlicesSanctuaryGoldPromotion,
  LazySlicesSanctuaryIncludedWithStay,
  LazySlicesSanctuaryIntroductionsTitleParagraph,
  LazySlicesSanctuaryRoomListing,
  LazySlicesSanctuarySpecialOffer,
  LazySlicesSanctuaryTwoColumnCopyAndImage,
  LazySlicesSanctuaryLeftRightHalfBlock,
  LazySlicesSandpiperAboutRoomes,
  LazySlicesSandpiperAccordionCopy,
  LazySlicesSandpiperActivityGrid,
  LazySlicesSanctuaryVideoTakeover,
  LazySlicesSandpiperActivityGridCard,
  LazySlicesSandpiperAlertBlock,
  LazySlicesSandpiperBackgroundShape,
  LazySlicesSandpiperColorCardGrid,
  LazySlicesSandpiperColorCardGridCard,
  LazySlicesSandpiperCopyBesideImages,
  LazySlicesSandpiperGeneralTitleAndCopy,
  LazySlicesSandpiperMap,
  LazySlicesSandpiperRoomType,
  LazySlicesSandpiperRoomTypes,
  LazySlicesSandpiperSandpiperHeroImage,
  LazySlicesSandpiperIncludedWithStay,
  LazySlicesSandpiperRoomTypeFilter,
  LazySlicesSandpiperSandpiperSpecialOfferSlider,
  LazySlicesSeadustContentSeadustBlog,
  LazySlicesSeadustContentSeadustBlogDetails,
  LazySlicesSeadustContentSeadustConferenceRoomFeature,
  LazySlicesSeadustContentSeadustContactForm,
  LazySlicesSeadustContentSeadustFullWidthGallery,
  LazySlicesSeadustContentSeadustConferenceRoom,
  LazySlicesSeadustContentSeadustGallery,
  LazySlicesSeadustContentSeadustGalleryList,
  LazySlicesSeadustContentSeadustGalleryListBlock,
  LazySlicesSeadustContentSeadustFullWidthGalleryImage,
  LazySlicesSeadustContentSeadustGenericFullWidthBgWhiteBox,
  LazySlicesSeadustContentSeadustGenericListItem,
  LazySlicesSeadustContentSeadustGenericListItemImage,
  LazySlicesSeadustContentSeadustMap,
  LazySlicesSeadustContentSeadustRestaurantList,
  LazySlicesSeadustContentSeadustRestaurantListBlock,
  LazySlicesSeadustContentSeadustRightParagraphGridImages,
  LazySlicesSeadustContentSeadustRoomDetails,
  LazySlicesSeadustContentSeadustSandyThreeColGridItem,
  LazySlicesSeadustContentSeadustServicesIconsList,
  LazySlicesSeadustContentSeadustSandyThreeColGrid,
  LazySlicesSeadustContentSeadustSafeVideoNavigation,
  LazySlicesSeadustContentSeadustServicesIconsListItem,
  LazySlicesSeadustContentSeadustSitemap,
  LazySlicesSeadustContentSeadustTitleParagraph,
  LazySlicesSeadustContentSeadustWeddingsForm,
  LazySlicesSeadustGraphicalLinksSeadustFiveImageGridLinks,
  LazySlicesSeadustFormsSeadustConferenceRoomRequestForm,
  LazySlicesSeadustSlidersFullWidthSlide,
  LazySlicesSeadustGraphicalLinksSeadustFiftyFiftyImageLinks,
  LazySlicesSeadustSlidersSeadustBlocksSlide,
  LazySlicesSeadustSlidersFullWidth,
  LazySlicesSeadustSlidersSeadustBlocksSlider,
  LazySlicesSeadustSlidersSeadustParalaxBlocksSlide,
  LazySlicesSeadustSlidersSeadustLeftAlignContentSlide,
  LazySlicesSeadustSlidersSeadustParalaxBlocksSlider,
  LazySlicesSeadustSlidersSeadustLeftAlignContentWithSlider,
  LazySlicesSeadustThreeSixtyToursSeadustSpaziousTour,
  LazySlicesWeddingsBgSideContentCallToAction,
  LazySlicesWeddingsContactDetails,
  LazySlicesWeddingsFullWidthImage,
  LazySlicesWeddingsCard,
  LazySlicesWeddingsPackagesSlider,
  LazySlicesWeddingsPackagesSlide,
  LazySlicesWeddingsPatternedGrid,
  LazySlicesWeddingsTwoColumnIntroduction,
  LazySlicesWeddingsVenueSlide,
  LazySlicesWeddingsTitleParagraphDivider,
  LazySlicesWeddingsVenueSlider,
  LazySlicesWyndhamCardOverImages,
  LazySlicesWyndhamDualImageCards,
  LazySlicesWyndhamFullWidthImageCardOnBottom,
  LazySlicesWyndhamHideSignUpToSave,
  LazySlicesWyndhamHideWyndhamInlineBookingWidget,
  LazySlicesWyndhamInlineBookingWidget,
  LazySlicesWyndhamTallSixImageCards,
  LazySlicesWyndhamTallThreeImageCards
}


const resolvedComponent = components[`Lazy${props.slice.component}`]

const { isTopLevelWindow } = useUtils()
const sliceId = computed(() => {
  return `voix-slice-${props.slice.id}`
})

const pageStore = usePageStore()
const adminStore = useAdminStore()
const sliceStore = useSliceStore()
const sliceHelper = useSlice()
const sliceConfig = sliceStore.getSlice(props.slice.component)

const fieldsProxy: Ref<Fields | null> = ref(null)

// Field Data
if (sliceConfig && props.slice.fields)
  fieldsProxy.value = sliceHelper.resolveFields(sliceConfig, props.slice.fields)

// We need to watch the fields for edits so that we can update the
// fieldsProxy value. This is necessary because we need to be able to
// be reactive to changes in the fields.
watch(
  () => props.slice,
  () => {
    if (sliceConfig && props.slice.fields)
      fieldsProxy.value = sliceHelper.resolveFields(sliceConfig, props.slice.fields)
  },
  { deep: true },
)
if (import.meta.hot) {
  import.meta.hot.on('voix-slice-update', (data) => {
    const key = Object.keys(data)[0]
    if (key === props.slice.component && sliceConfig && props.slice.fields) {
      setTimeout(() => {
        fieldsProxy.value = sliceHelper.resolveFields(sliceConfig, props.slice.fields)
      }, 1000)
    }
  })
}

// Slice Styles
const sliceStyles = ref({})
function generateSliceStyles() {
  const theSlice = Object.assign({}, props.slice)

  if (!props.slice)
    return { properties: { dom_id: '', settings: {} } }

  let styles: Record<string, string | number> = {}

  if (theSlice.properties && theSlice.properties.settings) {
    const breakpointSettings = findBreakpointSettings(
      theSlice.properties.settings,
    ) as SliceSettingsInterface | null

    if (breakpointSettings) {
      if (breakpointSettings.position)
        styles.position = breakpointSettings.position

      styles = {
        ...styles,
        ...buildFlexStyles(styles, breakpointSettings),
        ...buildGridStyles(styles, breakpointSettings),
        ...buildMaxWidthStyles(styles, breakpointSettings),
        ...buildMarginStyles(styles, breakpointSettings),
        ...buildPaddingStyles(styles, breakpointSettings),
        ...buildColorStyles(styles, breakpointSettings),
      }
    }
  }

  sliceStyles.value = styles
  return true
}
// Initial generation of styles
generateSliceStyles()

// Debounced updates to styles
const updateSliceStyles = useDebounceFn(
  generateSliceStyles,
  10,
  { maxWait: 50 },
)

// Reset styles on window resize
const { width: windowWidth, height: windowHeight } = useWindowSize()
watch([windowWidth, windowHeight], () => {
  updateSliceStyles()
})

const settings = computed(() => {
  if (props.slice.properties && props.slice.properties.settings)
    return props.slice.properties.settings

  return {}
})

watch(settings, () => {
  updateSliceStyles()
}, { deep: true })
const sliceRef = ref<any | null>(null)
provide('sliceRef', sliceRef)

const clickPosition = ref({ x: 0, y: 0, active: false })
function openContextMenu(e: MouseEvent) {
  if (isTopLevelWindow()) {
    e.preventDefault()

    adminStore.setCurrentContextItem(props.slice, e.clientX, e.clientY)
  }
}

// Setup for highlighting the slice that is being hovered over
const sliceRoot = ref<HTMLElement | null>(null)
const highlightStyles = ref({})
const highlightedElement = computed(() => {
  return adminStore.highlightElement
})

const isCurrentlyHightlighted = computed(() => {
  return (
    highlightedElement.value.type === props.slice.type
    && highlightedElement.value.id === props.slice.id
  )
})

watch(highlightedElement, () => {
  if (isCurrentlyHightlighted.value)
    highlightStyles.value = getHighlightStyles()
})

function getHighlightStyles() {
  const styles = {
    top: 'auto',
    left: 'auto',
    width: 'auto',
    height: 'auto',
    opacity: '0.5',
  }

  if (sliceRoot.value && typeof window !== 'undefined') {
    const { top, left, width, height } = sliceRoot.value?.getBoundingClientRect()

    styles.top = `${top}px`
    styles.left = `${left}px`
    styles.width = `${width}px`
    styles.height = `${height}px`
  }

  return styles
}

function closeContextMenu() {
  clickPosition.value.active = false
}

function selectElement(element: ElementInterface) {
  adminStore.setSelectedElement(element)
}

// Pass this down through PageNavigationSlice to PageNavigationField
provide(
  'slice',
  computed(() => {
    return props.slice
  }),
)

// TODO: Is this necessary? It may cause confusion in devtools
provide('fields', fieldsProxy.value)
provide('mountOpen', true)

function logError(error: any) {
  let msg = error

  if (Object.prototype.hasOwnProperty.call(error, 'message'))
    msg = error.message

  if (typeof window === 'object'
    && msg.includes('Failed to fetch dynamically imported module')
  ) {
    // very likely that we released a new version so let's try to refresh
    let url = window.location.href
    const queryString = new URLSearchParams(window.location.search)

    if (!queryString.has('v-fresh')) {
      // only if fresh is not on the page
      if (url.includes('?'))
        url += '&v-fresh=1'
      else
        url += '?v-fresh=1'

      window.location.href = url
    }
  }

  useEvent('voix:error', error)

  console.error(error)

  return error
}

const { $voix } = useNuxtApp()
const sliceInView = ref(false)
const countedTheView = ref(false)

watch(sliceRef, (el) => {
  if (el?.$el) {
    if (el?.$el?.nodeType === 1) {
      inView(el.$el, () => {
        if (!countedTheView.value)
          $voix.eventBus.emit('voix:slice:viewed', props.slice)

        sliceInView.value = true
        countedTheView.value = true

        return () => {
          sliceInView.value = false
        }
      })
    }
    else {
      console.info(`Root element of slice is not of type Element for ${props.slice.component}`)
    }
  }
})
</script>

<template>
  <NuxtErrorBoundary @error="logError">
    <template
      v-if="resolvedComponent"
      :key="slice.properties?.dom_id || sliceId"
    >
      <component
        :is="resolvedComponent"
        :id="slice.properties?.dom_id || sliceId"
        ref="sliceRef"
        :slice="slice"
        :fields="fieldsProxy"
        :style="sliceStyles"
        :in-view="sliceInView"
        v-bind="$attrs"
        @click.right="openContextMenu"
      >
        <template v-for="{ name } in sliceConfig?.slots" #[name]>
          <slot :name="name" />
        </template>
      </component>
    </template>
    <DevOnly v-else>
      The component {{ slice.component }} cannot be found. Are you sure you named it correctly?
    </DevOnly>
  </NuxtErrorBoundary>
</template>
